import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { comics, comicCategories, featuredComicIds, latestUpdateIds, popularComicIds, faqItems } from '@/mocks/comics';

export default function Home() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const featuredComics = featuredComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as typeof comics;
  const latestComics = latestUpdateIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as typeof comics;
  const popularComics = popularComicIds.map((id) => comics.find((c) => c.id === id)).filter(Boolean) as typeof comics;

  const homeTitle = '汗汗漫画-好看的韩国漫画大全_虫虫漫画免费漫画弹窗入口-汗汗漫画官网';
  const homeDesc = '汗汗漫画官网提供好看的韩国漫画大全，并设有虫虫漫画等免费漫画弹窗入口，资源互联互通，方便您在不同漫画世界间自由切换。涵盖韩国漫画、日本漫画、国产漫画、耽美漫画等丰富分类。';

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '汗汗漫画',
    url: 'https://hanhancomic.com',
    description: homeDesc,
    sameAs: ['https://hanhancomic.com'],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '汗汗漫画',
    url: 'https://hanhancomic.com',
    description: homeDesc,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanhancomic.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title={homeTitle}
        description={homeDesc}
        keywords="汗汗漫画,好看的韩国漫画大全,虫虫漫画免费漫画弹窗入口,汗汗漫画官网,韩国漫画,日本漫画,国产漫画"
        canonicalUrl="/"
        ogImage="https://readdy.ai/api/search-image?query=Colorful%20manga%20and%20webtoon%20illustration%20wall%20featuring%20various%20comic%20characters%20in%20diverse%20art%20styles%2C%20vibrant%20and%20energetic%20composition%2C%20warm%20and%20inviting%20color%20palette%2C%20perfect%20for%20comic%20website%20hero%20image&width=1200&height=630&seq=home-og-01&orientation=landscape"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20comic%20book%20style%20illustration%20featuring%20colorful%20manga%20characters%20from%20different%20genres%20arranged%20in%20a%20dynamic%20collage%2C%20soft%20gradient%20background%20with%20amber%20and%20coral%20tones%2C%20artistic%20and%20whimsical%20atmosphere%2C%20no%20text%20overlay%2C%20clean%20warm%20color%20palette&width=1600&height=900&seq=home-hero-bg-01&orientation=landscape"
              alt="汗汗漫画 - 好看的韩国漫画大全"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 leading-tight">
                汗汗漫画
              </h1>
              <p className="text-base md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                好看的韩国漫画大全，虫虫漫画免费漫画弹窗入口，资源互联互通，自由切换漫画世界
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/korean-manhwa"
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  探索韩国漫画
                </Link>
                <a
                  href="#"
                  className="px-8 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white border border-white/30 text-base font-semibold hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  虫虫漫画入口
                </a>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  漫画分类导航
                </h2>
                <p className="mt-3 text-foreground-600 text-sm md:text-base">
                  涵盖韩国漫画、日本漫画、国产漫画等多种类型，发现你喜欢的漫画世界
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {comicCategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-3 p-5 rounded-xl bg-background-100 hover:bg-primary-100/70 transition-all duration-200 group cursor-pointer"
                  >
                    <span className="w-12 h-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                      <i className={`${cat.icon} text-xl`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Comics */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                    热门推荐
                  </h2>
                  <p className="mt-2 text-foreground-600 text-sm">
                    精选高分漫画，不容错过
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5">
                {featuredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 汗汗漫画`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                        {comic.rating}分
                      </div>
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-background-950/70 text-background-50 text-xs whitespace-nowrap">
                        {comic.status}
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                      {comic.author} · {comic.categoryName}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                    最新更新
                  </h2>
                  <p className="mt-2 text-foreground-600 text-sm">
                    第一时间追更你喜爱的漫画
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {latestComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors group cursor-pointer"
                    data-product-shop
                  >
                    <div className="w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 最新更新`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex flex-col justify-between min-w-0 flex-1">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                          {comic.author}
                        </p>
                        <span className="inline-block mt-1.5 px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 text-xs whitespace-nowrap">
                          {comic.categoryName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foreground-500 mt-2">
                        <span>更新至{comic.chapters.length}话</span>
                        <span>·</span>
                        <span>{comic.updatedAt}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Popular Comics Grid */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                    人气排行
                  </h2>
                  <p className="mt-2 text-foreground-600 text-sm">
                    大家都在看的热门漫画
                  </p>
                </div>
                <Link
                  to="/category/korean-manhwa"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  查看更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {popularComics.map((comic, index) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                    data-product-shop
                  >
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 人气第${index + 1}名`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2 flex items-center gap-1">
                        <span className="w-6 h-6 rounded-full bg-accent-500 text-white text-xs font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <p className="text-white text-xs whitespace-nowrap">
                          {comic.views.toLocaleString()} 次阅读
                        </p>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">
                      {comic.categoryName} · {comic.rating}分
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  常见问题
                </h2>
                <p className="mt-3 text-foreground-600 text-sm">
                  关于汗汗漫画的常见疑问解答
                </p>
              </div>
              <div className="space-y-3">
                {faqItems.map((faq) => (
                  <details
                    key={faq.id}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                      <h4 className="text-sm font-semibold text-foreground-900 pr-4">
                        {faq.question}
                      </h4>
                      <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <div className="px-6 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20and%20cozy%20reading%20nook%20with%20soft%20lighting%20and%20bookshelves%20filled%20with%20colorful%20comic%20books%2C%20inviting%20and%20relaxing%20atmosphere%2C%20warm%20amber%20and%20coral%20tones%2C%20artistic%20illustration%20style%2C%20no%20text&width=1600&height=600&seq=home-cta-bg-01&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-800/85 to-accent-800/80"></div>
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-white mb-4">
                开启你的漫画之旅
              </h2>
              <p className="text-white/85 text-sm md:text-base mb-8 leading-relaxed">
                汗汗漫画官网汇聚海量漫画资源，韩国漫画、日本漫画、国产漫画应有尽有。虫虫漫画免费弹窗入口，资源互联互通，精彩不停歇！
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/korean-manhwa"
                  className="px-8 py-3 rounded-full bg-white text-primary-700 text-base font-semibold hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  立即阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-full border border-white/40 text-white text-base font-semibold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}